import React, { useState, useEffect } from "react";
import {
  Sparkles,
  X,
  FileText,
  Upload,
  AlertCircle,
  Loader2,
} from "lucide-react";
import QuestionBankClient from "../data/api/QuestionBankClient";
import CustomSelect, { SelectOption } from "./CustomSelect";
import "../styles/QuestionBankStyles.css";
import ReactDOM from "react-dom";

interface GenerateAIQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (requestId: string, moduleId: string) => void;
  modules: { module_id: string; module_name: string }[];
  initialModuleId?: string;
}

const GenerateAIQuestionModal: React.FC<GenerateAIQuestionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  modules,
  initialModuleId = "",
}) => {
  const [selectedModule, setSelectedModule] = useState<string>(initialModuleId);
  const [contextType, setContextType] = useState<"text" | "file">("text");
  const [promptText, setPromptText] = useState<string>("");
  const [noOfQuestions, setNoOfQuestions] = useState<number>(5);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState<boolean>(false);

  useEffect(() => {
    if (initialModuleId) {
      setSelectedModule(initialModuleId);
    } else if (modules.length > 0 && !selectedModule) {
      setSelectedModule(modules[0].module_id);
    }
  }, [initialModuleId, modules]);

  if (!isOpen) return null;

  const moduleOptions: SelectOption[] = modules.map((m) => ({
    value: m.module_id,
    label: m.module_name,
  }));

  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB limit
  const ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx"];

  const validateFile = (file: File): boolean => {
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setErrorMsg(
        `Invalid file format '${ext}'. Supported formats: ${ALLOWED_EXTENSIONS.join(", ")}`,
      );
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      setErrorMsg(
        `File size exceeds 25MB limit (${(file.size / 1024 / 1024).toFixed(2)} MB). Please select a file smaller than 25MB.`,
      );
      return false;
    }
    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
        setErrorMsg(null);
      } else {
        e.target.value = "";
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
        setErrorMsg(null);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleGenerate = async () => {
    setErrorMsg(null);
    if (!selectedModule) {
      setErrorMsg("Please select a Target Module for generated questions.");
      return;
    }
    if (contextType === "text" && !promptText.trim()) {
      setErrorMsg(
        "Please provide a topic description or context text for AI question generation.",
      );
      return;
    }
    if (contextType === "file" && !selectedFile) {
      setErrorMsg("Please upload a context document (PDF or Text file).");
      return;
    }

    try {
      setIsGenerating(true);
      const modObj = modules.find((m) => m.module_id === selectedModule);

      let contextFileUrl = "";
      let contextFileName = "";

      if (contextType === "file" && selectedFile) {
        contextFileName = selectedFile.name;
        // Step 1: Call /create_upload_url to get S3 uploadURL and downloadURL
        const uploadRes = await QuestionBankClient.createUploadUrl(
          selectedFile.name,
        );

        // Step 2: Upload file blob directly to S3 uploadURL
        if (uploadRes && uploadRes.uploadURL) {
          await QuestionBankClient.uploadFileToS3(
            uploadRes.uploadURL,
            selectedFile,
          );
          contextFileUrl = uploadRes.downloadURL || uploadRes.uploadURL;
        } else {
          throw new Error("Failed to obtain upload URL from server.");
        }
      }

      const res = await QuestionBankClient.generateAIQuestions({
        module_id: selectedModule,
        module_name: modObj ? modObj.module_name : "General",
        context_type: contextType,
        user_prompt: promptText.trim(),
        context_text: promptText.trim(),
        context_file_name: contextFileName,
        context_file: contextFileUrl,
        no_of_questions_requested: noOfQuestions,
      });

      setIsGenerating(false);
      onClose();
      if (onSuccess) {
        onSuccess(res?.request?.request_id || "", selectedModule);
      }
    } catch (err: any) {
      setIsGenerating(false);
      setErrorMsg(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to generate AI questions.",
      );
    }
  };

  return ReactDOM.createPortal(
    <div className="qb-modal-overlay">
      <div className="qb-modal-card" style={{ maxWidth: "640px" }}>
        <div
          className="qb-modal-header"
          style={{
            background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
            color: "#FFF",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Sparkles size={22} style={{ color: "#FCD34D" }} />
            <div>
              <h3
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#FFF",
                }}
              >
                Generate Questions
              </h3>
              <p style={{ margin: 0, fontSize: "12px", opacity: 0.85 }}>
                Auto-create high quality MCQs from topic context or textbook
                documents
              </p>
            </div>
          </div>
          <button
            className="qb-modal-close"
            style={{ color: "#FFF" }}
            onClick={onClose}
            disabled={isGenerating}
          >
            <X size={20} />
          </button>
        </div>

        <div className="qb-modal-body" style={{ padding: "24px" }}>
          {errorMsg && (
            <div
              style={{
                padding: "12px 16px",
                borderRadius: "8px",
                backgroundColor: "#FEF2F2",
                border: "1px solid #FECACA",
                color: "#991B1B",
                fontSize: "13px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "16px",
              }}
            >
              <AlertCircle size={16} />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Module Selector */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: 600,
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Target Module <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <CustomSelect
              options={moduleOptions}
              value={selectedModule}
              onChange={(val) => setSelectedModule(val)}
              placeholder="Select Target Module..."
              disabled={isGenerating}
            />
          </div>

          {/* Context Source Tabs */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: 600,
                color: "#374151",
                marginBottom: "8px",
              }}
            >
              Context Source <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                type="button"
                className={`qb-tab-btn ${contextType === "text" ? "active" : ""}`}
                onClick={() => setContextType("text")}
                disabled={isGenerating}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  borderRadius: "8px",
                  border: "1px solid #E5E7EB",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                <FileText size={16} />
                <span>Text Prompt / Topic Context</span>
              </button>
              <button
                type="button"
                className={`qb-tab-btn ${contextType === "file" ? "active" : ""}`}
                onClick={() => setContextType("file")}
                disabled={isGenerating}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  borderRadius: "8px",
                  border: "1px solid #E5E7EB",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                <Upload size={16} />
                <span>Document Upload (PDF / DOC)</span>
              </button>
            </div>
          </div>

          {/* Text Context Input */}
          {contextType === "text" ? (
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: "6px",
                }}
              >
                Topic Description or Raw Context Text{" "}
                <span style={{ color: "#EF4444" }}>*</span>
              </label>
              <textarea
                className="qb-form-textarea"
                rows={4}
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                placeholder="e.g. Java Virtual Machine (JVM) memory architecture, Garbage Collection algorithms, Heap vs Stack memory management..."
                disabled={isGenerating}
                style={{
                  width: "100%",
                  borderRadius: "8px",
                  border: "1px solid #D1D5DB",
                  padding: "10px 12px",
                  fontSize: "13px",
                }}
              />
            </div>
          ) : (
            /* Document Upload Zone */
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: "6px",
                }}
              >
                Upload File <span style={{ color: "#EF4444" }}>*</span>
              </label>
              <div
                style={{
                  border: `2px dashed ${isDragActive ? "#0D6E5B" : "#438E76"}`,
                  borderRadius: "12px",
                  padding: "24px",
                  textAlign: "center",
                  backgroundColor: isDragActive ? "#E6F4F1" : "#DDF2EB",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                }}
                onClick={() =>
                  document.getElementById("ai-file-input")?.click()
                }
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "12px",
                    marginBottom: "8px",
                  }}
                >
                  <Upload size={36} style={{ color: "#0D6E5B" }} />
                  <div style={{ textAlign: "left" }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "15px",
                        fontWeight: 600,
                        color: "#1E293B",
                      }}
                    >
                      {selectedFile
                        ? selectedFile.name
                        : "Select a PDF/DOC file to upload questions"}
                    </p>
                    <p
                      style={{
                        margin: "2px 0 0",
                        fontSize: "13px",
                        color: "#64748B",
                      }}
                    >
                      {selectedFile
                        ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`
                        : "or drag and drop here"}
                    </p>
                  </div>
                </div>
                <input
                  id="ai-file-input"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                  disabled={isGenerating}
                />
              </div>

              <ul
                style={{
                  margin: "10px 0 0 0",
                  paddingLeft: "18px",
                  fontSize: "13px",
                  color: "#475569",
                  lineHeight: "1.6",
                }}
              >
                <li>Supported format: .pdf, .doc, .docx</li>
                <li>File Size: Less than 25MB</li>
              </ul>
            </div>
          )}

          {/* Number of Questions */}
          <div style={{ marginBottom: "20px" }}>
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: "6px",
                }}
              >
                Number of Questions
              </label>
              <input
                type="number"
                min={1}
                max={20}
                className="qb-form-input"
                value={noOfQuestions}
                onChange={(e) =>
                  setNoOfQuestions(parseInt(e.target.value) || 5)
                }
                disabled={isGenerating}
                style={{
                  width: "100%",
                  borderRadius: "8px",
                  border: "1px solid #D1D5DB",
                  padding: "9px 12px",
                  fontSize: "13px",
                }}
              />
            </div>
          </div>
        </div>

        {(() => {
          const isModalValid = Boolean(
            selectedModule &&
            (contextType === "text"
              ? promptText.trim()
              : Boolean(selectedFile)) &&
            noOfQuestions > 0,
          );
          return (
            <div
              className="qb-modal-footer"
              style={{
                padding: "16px 24px",
                borderTop: "1px solid #E5E7EB",
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                backgroundColor: "#F9FAFB",
              }}
            >
              <button
                type="button"
                className="qb-btn-secondary"
                onClick={onClose}
                disabled={isGenerating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="qb-btn-primary"
                style={{
                  background:
                    "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                  borderColor: "#4F46E5",
                  color: "#FFF",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onClick={handleGenerate}
                disabled={isGenerating || !isModalValid}
              >
                {isGenerating ? (
                  <>
                    <Loader2 size={16} className="qb-spin-icon" />
                    <span>Generating Questions...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    <span>Generate Questions</span>
                  </>
                )}
              </button>
            </div>
          );
        })()}
      </div>
    </div>,
    document.body,
  );
};

export default GenerateAIQuestionModal;
